// apps/provider/src/services/api/support.js
// Support requests, messages, attachments.
//
// Methods: getSupportRequests, getSupportRequest, createSupportRequest,
//          getSupportMessages, addSupportMessage
//
// Response types: SupportRequest, SupportMessage

/**
 * @returns {Object} Support domain methods (bound to `this` of the composed apiService)
 */
export function createSupportMethods() {
  const fileToDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  return {

    /**
     * Get all support requests for a user.
     * Xano: GET /support_request
     * @param {string} userId
     */
    async getSupportRequests(userId) {
      return this.request(`/support_request?user_id=${userId}`, { method: 'GET' });
    },

    /**
     * Get a single support request by ID.
     * Xano: GET /support_request/{id}
     * @param {number} supportRequestId
     */
    async getSupportRequest(supportRequestId) {
      return this.request(`/support_request/${supportRequestId}`, { method: 'GET' });
    },

    /**
     * Create a new support request.
     * Xano: POST /support_request
     * @param {Object} requestData
     */
    async createSupportRequest(requestData) {
      let attachments = [];
      if (requestData.attachments?.length) {
        attachments = await Promise.all(
          requestData.attachments.map(file => fileToDataURL(file))
        );
      }

      const payload = {
        user_id: requestData.user_id,
        organization_id: requestData.organization_id,
        type: requestData.type,
        priority: requestData.priority,
        subject: requestData.subject,
        description: requestData.description,
        status: 'Open',
        raw_attachments: attachments
      };

      if (requestData.solution_id) payload.solution_id = requestData.solution_id;
      if (requestData.location_id) payload.location_id = requestData.location_id;

      const request = await this.request('/support_request', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      await this.logActivity(
        requestData.user_id,
        requestData.organization_id,
        requestData.location_id || null,
        'SUPPORT_REQUEST',
        'support',
        request.id,
        `Support request created: ${request.subject}`,
        {
          priority: request.priority,
          type: request.type,
          solution_id: request.solution_id || null
        },
        'completed',
        '/support'
      );

      return request;
    },

    /**
     * Get messages for a support request.
     * Xano: GET /support-messages
     * @param {number} supportRequestId
     */
    async getSupportMessages(supportRequestId) {
      return this.request(`/support-messages?support_request_id=${supportRequestId}`, { method: 'GET' });
    },

    /**
     * Add a message to a support request.
     * Xano: POST /support-messages
     * @param {number} supportRequestId
     * @param {string} body
     * @param {File[]} [attachments]
     */
    async addSupportMessage(supportRequestId, body, attachments = []) {
      let rawAttachments = [];
      if (attachments.length) {
        rawAttachments = await Promise.all(
          attachments.map(file => fileToDataURL(file))
        );
      }

      return this.request('/support-messages', {
        method: 'POST',
        body: JSON.stringify({
          support_request_id: supportRequestId,
          body,
          raw_attachments: rawAttachments
        })
      });
    },
  };
}
